import { compact } from 'lodash';

import {
  useCanUserConnectToAddepar,
  useIsUserLoggedIntoAddepar,
} from '@/modules/assetProviderIntegrations/addepar/hooks/useIsAddeparEnabled';
import {
  useCanUserConnectToBlackDiamond,
  useIsUserLoggedIntoBlackDiamond,
} from '@/modules/assetProviderIntegrations/blackDiamond/hooks/useIsBlackDiamondEnabled';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { UserKind } from '@/types/schema';

import {
  useCanUserConnectToEmoney,
  useIsUserLoggedIntoEmoney,
} from '../../emoney/hooks/useIsEmoneyEnabled';
import {
  useCanUserConnectToOrion,
  useIsUserLoggedIntoOrion,
} from '../../orion/hooks/useIsOrionEnabled';
import {
  useCanUserConnectToSnowflake,
  useIsUserLoggedIntoSnowflake,
} from '../../snowflake/hooks/useIsSnowflakeEnabled';
import {
  AssetIntegrationProviders,
  getIntegrationProviderDisplayName,
} from '../constants';

export interface AssetProviderDisplayNameOpts {
  /** if true, the casing of the display name will support being included in the middle of a sentence */
  isInline: boolean;
}

export function useAssetProviderDisplayName(
  opts: AssetProviderDisplayNameOpts
) {
  const { canConnectToIntegrations } = useIsAssetProviderIntegrationEnabled();
  if (canConnectToIntegrations.length > 1) {
    return opts.isInline ? 'asset providers' : 'Asset providers';
  }

  if (canConnectToIntegrations.includes(AssetIntegrationProviders.ADDEPAR)) {
    return getIntegrationProviderDisplayName(AssetIntegrationProviders.ADDEPAR);
  }

  if (
    canConnectToIntegrations.includes(AssetIntegrationProviders.BLACK_DIAMOND)
  ) {
    return getIntegrationProviderDisplayName(
      AssetIntegrationProviders.BLACK_DIAMOND
    );
  }

  if (canConnectToIntegrations.includes(AssetIntegrationProviders.ORION)) {
    return getIntegrationProviderDisplayName(AssetIntegrationProviders.ORION);
  }

  if (canConnectToIntegrations.includes(AssetIntegrationProviders.EMONEY)) {
    return getIntegrationProviderDisplayName(AssetIntegrationProviders.EMONEY);
  }

  if (canConnectToIntegrations.includes(AssetIntegrationProviders.SNOWFLAKE)) {
    return getIntegrationProviderDisplayName(
      AssetIntegrationProviders.SNOWFLAKE
    );
  }

  return null;
}

/**
 * Returns a list of third-party integrations which a user has connected to and can start using;
 * eg: for linking households/entities.
 */
export function useLoggedInAssetsIntegrations(): AssetIntegrationProviders[] {
  const shouldExposeAddeparIntegration = useIsUserLoggedIntoAddepar();
  const shouldExposeBlackDiamondIntegration = useIsUserLoggedIntoBlackDiamond();
  const shouldExposeOrionIntegration = useIsUserLoggedIntoOrion();
  const shouldExposeEmoneyIntegration = useIsUserLoggedIntoEmoney();
  const shouldExposeSnowflakeIntegration = useIsUserLoggedIntoSnowflake();
  return compact([
    shouldExposeAddeparIntegration && AssetIntegrationProviders.ADDEPAR,
    shouldExposeBlackDiamondIntegration &&
      AssetIntegrationProviders.BLACK_DIAMOND,
    shouldExposeOrionIntegration && AssetIntegrationProviders.ORION,
    shouldExposeEmoneyIntegration && AssetIntegrationProviders.EMONEY,
    shouldExposeSnowflakeIntegration && AssetIntegrationProviders.SNOWFLAKE,
  ]);
}

/**
 * Returns a list of third-party integrations to which a user can connect their individual account;
 * eg: tenant has the integration set up, but a user hasn't gone through the connection flow.
 */
function useUserCanConnectToIntegrations(): AssetIntegrationProviders[] {
  const canConnectToAddepar = useCanUserConnectToAddepar();
  const canConnectToBlackDiamond = useCanUserConnectToBlackDiamond();
  const canConnectToOrion = useCanUserConnectToOrion();
  const canConnectToEmoney = useCanUserConnectToEmoney();
  const canConnectToSnowflake = useCanUserConnectToSnowflake();

  return compact([
    canConnectToAddepar && AssetIntegrationProviders.ADDEPAR,
    canConnectToBlackDiamond && AssetIntegrationProviders.BLACK_DIAMOND,
    canConnectToOrion && AssetIntegrationProviders.ORION,
    canConnectToEmoney && AssetIntegrationProviders.EMONEY,
    canConnectToSnowflake && AssetIntegrationProviders.SNOWFLAKE,
  ]);
}

export interface AssetProviderIntegrationEnabledReturnType {
  /** Reports whether or not a user has connected to a third-party data provider. */
  hasConnectedIntegration: boolean;
  /** A list of third-party data integrations to which a user has connected. */
  integrationsConnected: AssetIntegrationProviders[];
  /** A list of third-party data integrations set up for the tenant to which a user can connect. */
  canConnectToIntegrations: AssetIntegrationProviders[];
}

/**
 * Check the state of the user and the tenant to determine the user's status for third-party data
 * integrations.
 *
 * An integration can exist in one of three states:
 *
 * - Not set up for the tenant, and unable to be connected to (integration will not be present in either list)
 * - Set up for the tenant, but not connected to on the user level (integration will be present in `canConnectToIntegrations` but not `integrationsConnected`)
 * - Set up for the tenant and connected to on the user level (integration _should_ be present in both lists)
 *
 * If a user is connected to any integration, the `hasConnectedIntegration` field will be true.
 */
export function useIsAssetProviderIntegrationEnabled(): AssetProviderIntegrationEnabledReturnType {
  const integrationsConnected: AssetIntegrationProviders[] =
    useLoggedInAssetsIntegrations();
  const canConnectToIntegrations: AssetIntegrationProviders[] =
    useUserCanConnectToIntegrations();

  const hasConnectedIntegration = integrationsConnected.length > 0;

  return {
    hasConnectedIntegration,
    integrationsConnected,
    canConnectToIntegrations,
  };
}

export interface AssetProviderDataLinkingState
  extends AssetProviderIntegrationEnabledReturnType {
  /** Providers whose cached data the current user can link. */
  integrationsAvailableForDataLinking: AssetIntegrationProviders[];
}

export function getIntegrationsAvailableForDataLinking(
  userKind: UserKind | undefined,
  integrationsConnected: AssetIntegrationProviders[],
  canConnectToIntegrations: AssetIntegrationProviders[]
): AssetIntegrationProviders[] {
  // Internal users work across customer tenants and do not have personal OAuth
  // connections there. The tenant integration still syncs the data they link.
  if (userKind === UserKind.LuminaryInternal) {
    return canConnectToIntegrations;
  }

  return integrationsConnected;
}

/**
 * Returns the provider state used when linking cached integration data.
 * Customer users still need a personal provider connection. Luminary internal
 * users can use any provider configured for the tenant.
 */
export function useAssetProviderDataLinkingState(): AssetProviderDataLinkingState {
  const currentUser = useCurrentUser();
  const integrationState = useIsAssetProviderIntegrationEnabled();

  return {
    ...integrationState,
    integrationsAvailableForDataLinking: getIntegrationsAvailableForDataLinking(
      currentUser?.authorization.userKind,
      integrationState.integrationsConnected,
      integrationState.canConnectToIntegrations
    ),
  };
}
